/**
 * Custom hooks per Document Schema Rules
 */
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import type {
    DocumentSchemaRuleCreate,
    DocumentSchemaRuleListFilters,
    DocumentSchemaRuleResponse,
    DocumentSchemaRuleUpdate,
} from "@/types/api/document-schemas";

const SCHEMA_RULES_QUERY_KEYS = {
    all: ["schema-rules"] as const,
    lists: () => [...SCHEMA_RULES_QUERY_KEYS.all, "list"] as const,
    list: (schemaId: string, filters?: DocumentSchemaRuleListFilters) => [...SCHEMA_RULES_QUERY_KEYS.lists(), schemaId, filters] as const,
    details: () => [...SCHEMA_RULES_QUERY_KEYS.all, "detail"] as const,
    detail: (schemaId: string, ruleId: string) => [...SCHEMA_RULES_QUERY_KEYS.details(), schemaId, ruleId] as const,
};

/**
 * Hook per lista rules di uno schema
 */
export function useSchemaRules(
    schemaId: string,
    filters?: DocumentSchemaRuleListFilters,
    options?: Omit<UseQueryOptions<DocumentSchemaRuleResponse[], Error>, "queryKey" | "queryFn">,
) {
    return useQuery({
        queryKey: SCHEMA_RULES_QUERY_KEYS.list(schemaId, filters),
        queryFn: async (): Promise<DocumentSchemaRuleResponse[]> => {
            return await apiGet<DocumentSchemaRuleResponse[]>(`/document-schemas/${schemaId}/rules`, {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        enabled: !!schemaId,
        ...options,
    });
}

/**
 * Hook per singola rule
 */
export function useSchemaRule(schemaId: string, ruleId: string) {
    return useQuery({
        queryKey: SCHEMA_RULES_QUERY_KEYS.detail(schemaId, ruleId),
        queryFn: async (): Promise<DocumentSchemaRuleResponse> => {
            return await apiGet<DocumentSchemaRuleResponse>(`/document-schemas/${schemaId}/rules/${ruleId}`);
        },
        enabled: !!schemaId && !!ruleId,
    });
}

/**
 * Hook per creare nuova rule
 */
export function useCreateSchemaRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ schemaId, data }: { schemaId: string; data: DocumentSchemaRuleCreate }): Promise<DocumentSchemaRuleResponse> => {
            return await apiPost<DocumentSchemaRuleResponse>(`/document-schemas/${schemaId}/rules`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SCHEMA_RULES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SCHEMA_RULES_QUERY_KEYS.list(variables.schemaId),
            });
        },
    });
}

/**
 * Hook per aggiornare rule esistente
 */
export function useUpdateSchemaRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            schemaId,
            ruleId,
            data,
        }: {
            schemaId: string;
            ruleId: string;
            data: DocumentSchemaRuleUpdate;
        }): Promise<DocumentSchemaRuleResponse> => {
            return await apiPut<DocumentSchemaRuleResponse>(`/document-schemas/${schemaId}/rules/${ruleId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SCHEMA_RULES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SCHEMA_RULES_QUERY_KEYS.list(variables.schemaId),
            });
            queryClient.invalidateQueries({
                queryKey: SCHEMA_RULES_QUERY_KEYS.detail(variables.schemaId, variables.ruleId),
            });
        },
    });
}

/**
 * Hook per eliminare rule (soft delete)
 */
export function useDeleteSchemaRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ schemaId, ruleId }: { schemaId: string; ruleId: string }): Promise<void> => {
            return await apiDelete<void>(`/document-schemas/${schemaId}/rules/${ruleId}`);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SCHEMA_RULES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SCHEMA_RULES_QUERY_KEYS.list(variables.schemaId),
            });
        },
    });
}

/**
 * Hook per duplicare rule
 */
export function useDuplicateSchemaRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            schemaId,
            ruleId,
            newVersion,
        }: {
            schemaId: string;
            ruleId: string;
            newVersion?: string;
        }): Promise<DocumentSchemaRuleResponse> => {
            const params = newVersion ? { new_version: newVersion } : undefined;
            return await apiPost<DocumentSchemaRuleResponse>(`/document-schemas/${schemaId}/rules/${ruleId}/duplicate`, undefined, { params });
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SCHEMA_RULES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SCHEMA_RULES_QUERY_KEYS.list(variables.schemaId),
            });
        },
    });
}
